<template>
	<div class="subviewOpts comments-opts">
		<span>
			<a href="#" class="button-link button-link-cicon" @click.prevent="addComment">
				<sym-icon name="add-comment"/>
				<span id="comments_add_label" style="padding-left: 8px;">{{ env.msg("menu.addComment") }}</span>
			</a>
		</span>
		<span v-if="hasPrev || hasNext" style="display:flex; column-gap:8px;">
			<span v-if="hasPrev" @click="goPrev">
				<sym-icon name="page-prev-16-bold" class="link-like-fi"/>
			</span>
			<span v-else style="opacity:0.3"><sym-icon name="page-prev-16-bold"/></span>

			<span v-if="hasNext" @click="goNext">
				<sym-icon name="page-next-16-bold" class="link-like-fi"/>
			</span>
			<span v-else style="opacity:0.3"><sym-icon name="page-next-16-bold"/></span>
		</span>
		<anchor-element tag="span" :chain="chain" style="margin-right: 4px;">
			<sym-icon :name="agenda.meta.muted || agenda.meta.disabled ? 'muted' : 'unmuted'" class="activeclick" @click="chain.toggle"/>
		</anchor-element>
		<boat-element v-if="chain.visible" :chain="chain" tag="div" class="menu-panel boat tail-top-right">
			<div v-if="agenda.meta.disabled" class="menu-panel-body">
				<a href="#" @click.stop.prevent="enable()" class="menu-item">
					<span class="mi mi-label">{{ env.msg("mute.enableNotifications") }}</span>
				</a>
			</div>
			<div v-else-if="agenda.meta.muted" class="menu-panel-body">
				<a href="#" @click.stop.prevent="unmute()" class="menu-item">
					<span class="mi mi-label">{{ env.msg("mute.cancel") }}</span>
				</a>
			</div>
			<div v-else class="menu-panel-body">
				<a href="#" @click.stop.prevent="mute(24*60*60*1000)" class="menu-item">
					<span class="mi mi-label">{{ env.msg("mute.tomorrow") }}</span>
				</a>
				<a href="#" @click.stop.prevent="mute(7*24*60*60*1000)" class="menu-item">
					<span class="mi mi-label">{{ env.msg("mute.week") }}</span>
				</a>
				<a href="#" @click.stop.prevent="mute(0)" class="menu-item">
					<span class="mi mi-label">{{ env.msg("mute.forever") }}</span>
				</a>
				<a href="#" @click.stop.prevent="disable()" class="menu-item">
					<span class="mi mi-label">{{ env.msg("mute.disableNotifications") }}</span>
				</a>
			</div>
		</boat-element>
	</div>
	<div ref="commentsContainer" class="comments">
		<div v-for="comment in comments" class="comment-row" :key="comment.id">
			<img :src="'avatar/'+noteUser(comment)" class="chat-message-avatar" :title="userName(noteUser(comment))">
			<div>
				<template v-if="comment.typeClassic">
					<crm-comment pin :src-type="srcType" :src-id="srcId" :comment-id="comment.id"
								 :for-customer="!!comment.forCustomer" :disabled="!canEdit(comment)"
								 :pin-types="comment.types?comment.types.toString():''" v-html="contentHtml(comment)"></crm-comment>
				</template>
				<template v-else-if="comment.typeForceInfo">
					<crm-comment :src-type="srcType" :src-id="srcId" :comment-id="comment.id"
								 :for-customer="!!comment.forCustomer" system-comment disabled>
						<div style="font-size:12px;margin-bottom:8px">{{ lastModified(comment) }} <span v-html="comment.message"></span></div>
						<div style="line-height: 18px">
							<sym-icon :name="forceInfoIcon(comment)" style="margin-right:4px"/>
							<span v-html="contentHtml(comment)"></span></div>
					</crm-comment>
				</template>
				<div class="comment-info">{{ infoOwner(comment) }}</div>
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import {ref, computed, onMounted, watch, watchEffect} from 'vue';
import {injectEnv, userInRole} from "@fd/Environment";

import SymIcon from "@fd/helpers/SymIcon.vue";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import {createChain} from "@/ui/paneltree/PanelTree";

import type {Comment} from "@t/note/Comment";
import type {AgendaData} from "@t/agenda/Agenda";
import type {SimplifiedUser} from "@t/user/SimplifiedUser";
import {insertNewNoteEditor} from "@/wc/CrmComment.js";
import {ContextKey, getIntValue, injectContext} from "@t/fd/AppContext.ts";
import {loadSourceType, SourceType} from "@t/meta/SourceType.ts";
import {UserRef} from "@t/util/CommonTypes.ts";


const props = defineProps<{
	srcType?: number,
	srcId?: string|number,
	endpoint?: string
}>();

const env = injectEnv();
const allUsers = ref<SimplifiedUser[]>([]);
const agenda = ref<AgendaData<Comment>>({ position: 0, total:0, size:0, items:[], meta: {muted: false} });
const pos = ref<number>(0);

const commentsContainer = ref(null);

const chain = createChain({
	rule: {
		ah: 100, av: 100,
		bh: 100, bv: 0,
		x: 8, y: 0
	}
}, "mute-menu");

const ctx = injectContext();
const sourceType = computed<number>(() => props.srcType ?? getIntValue(ctx.value, ContextKey.SRC_TYPE));
const sourceId = computed<string|number>(() => props.srcId ?? getIntValue(ctx.value, ContextKey.SRC_ID));
const type = ref<SourceType>(null);

function setEndpointId(endpoint: string, id: number | string):string {
	return endpoint.replace("{id}", id.toString());
}

watch(sourceType, async () => {
	if (!sourceType.value) return;
	if (type.value == null || type.value.id !== sourceType.value) {
		type.value = await loadSourceType(env, sourceType.value);
	}
}, { immediate: true });

const backendEndpoint = computed<string|null>(() => {
	if (props.endpoint) return props.endpoint;
	if (type.value && sourceId.value) {
		let ep = "api"+type.value.edit+"/comments";
		if (type.value.comments) ep = "api" + type.value.comments;
		return setEndpointId(ep, sourceId.value);
	}
	return null;
})

watchEffect(() => {
	if (backendEndpoint.value) {
		let url = env.net.urlParam(backendEndpoint.value, "meta", "true");
		url = env.net.urlParam(url, "position", pos.value);
		env.net.fetch(url)
			.then(response => response.json())
			.then(data => {
				agenda.value = data;
			})
			.catch((error) => {
				console.log("Error loading comments:" + error.message);
			});
	}
});


const storageKey = computed<string>(() => {
	return "comments-" + sourceType.value + "-" + sourceId.value;
});

const hasPrev = computed<boolean>(() => {
	return agenda.value.position > 0;
});

const hasNext = computed<boolean>(() => {
	return agenda.value.position + agenda.value.size < agenda.value.total;
});

const comments = computed<Comment[]>(() => {
	if (agenda.value.items) {
		return agenda.value.items;
	} else return [];
});

function userName(login: UserRef): string {
	let found = allUsers.value.find(user => {
		if (typeof login === "string") return user.login === login;
		else return user.id === login;
	});
	return found ? found.name : login.toString();
}

function addComment(): void {
	insertNewNoteEditor(commentsContainer.value, env.user.login, env.user.name, sourceType.value, sourceId.value);
}

function noteUser(comment: Comment): UserRef {
	return comment.modifiedBy ? comment.modifiedBy : comment.enteredBy;
}

function infoOwner(comment: Comment): string {
	let info = "";

	if (comment.modified) {
		info += env.msg("agenda.modified") + ": ";
		info += new Date(comment.modified).toLocaleDateString();
		info += " (" + userName(comment.modifiedBy) + ")";
	} else if (comment.entered != null) {
		info += env.msg("agenda.created") + ": ";
		info += new Date(comment.entered).toLocaleDateString();
		info += " (" + userName(comment.enteredBy) + ")";
	}

	return info;
}

function canEdit(comment: Comment): boolean {
	return comment.enteredBy === env.user.login ||
			userInRole(env.user, "canAdmin") ||
			userInRole(env.user, "canEditNotes");
}

function lastModified(comment: Comment): string {
	return comment.modified ? new Date(comment.modified).toLocaleDateString() : new Date(comment.entered).toLocaleDateString();
}

function forceInfoIcon(comment: Comment): string {
	return comment.types.includes(3) ? "thumb-down{color:#FF0000}" : "thumb-up{color:#008000}";
}

function contentHtml(comment: Comment): string {
	return comment.html ? comment.content : comment.contentHtml;
}

function goPrev(): void {
	if (hasPrev.value) {
		let newPosition: number = agenda.value.position - agenda.value.size;
		if (newPosition < 0) newPosition = 0;
		pos.value = newPosition;
		sessionStorage.setItem(storageKey.value, newPosition.toString());
	}
}

function goNext(): void {
	if (hasNext.value) {
		let newPosition: number = agenda.value.position + agenda.value.size;
		pos.value = newPosition;
		sessionStorage.setItem(storageKey.value, newPosition.toString());
	}
}


function mute(milis: number): void {
	env.net.fetch(backendEndpoint.value + "/mute?mutedTo=" + milis)
		.then(response => response.json())
		.then(data => {
			agenda.value.meta.muted = data.muted;
			chain.hide();
			//console.log("Get data:"+JSON.stringify(data));
		})
		.catch((error) => {
			console.log("Error muting comments with value:" + milis + " - " + error.message);
		});
}

function disable(): void {
	env.net.fetch(backendEndpoint.value + "/disable")
		.then(response => response.json())
		.then(data => {
			agenda.value.meta.disabled = data.disabled;
			chain.hide();
			//console.log("Get data:"+JSON.stringify(data));
		})
		.catch((error) => {
			console.log("Error disabling notifications - " + error.message);
		});
}

function enable(): void {
	env.net.fetch(backendEndpoint.value + "/enable")
		.then(response => response.json())
		.then(data => {
			agenda.value.meta.disabled = !data.enabled;
			chain.hide();
			//console.log("Get data:"+JSON.stringify(data));
		})
		.catch((error) => {
			console.log("Error disabling notifications - " + error.message);
		});
}

function unmute(): void {
	env.net.fetch(backendEndpoint.value + "/unmute")
		.then(response => response.json())
		.then(data => {
			agenda.value.meta.muted = data.muted;
			chain.hide();
			//console.log("Get data:"+JSON.stringify(data));
		})
		.catch((error) => {
			console.log("Error unmuting comments:" + error.message);
		});
}

async function init():Promise<void> {
	allUsers.value = await env.users();
}

//on create init and load comments
onMounted(() => init());
</script>
<style>
.comments-opts {
	justify-content: space-between !important;
	flex-flow: row wrap;
}

@container (width < 330px) {
	#comments_add_label {
		display: none;
	}
}
</style>